/**
 * @module Bn3thHeader
 * @description Singleton module that manages the BN3TH site header.
 */
const Bn3thHeader = (() => {
  /**
   * @private
   * @type {boolean}
   */
  const LOGGING = true;

  /**
   * @private
   * @type {object}
   */
  const SETTINGS = {
    name: "bn3th-header.js",
    megaMenuCloseDelay: 500,
  };

  /**
   * @type {object}
   */
  const SELECTORS = {
    siteHeader: ".bn3th-site-header",
    megaMenu: ".bn3th-mega-menu",
    megaMenuTrigger: ".bn3th-mega-menu__trigger",
    megaMenuPanel: ".bn3th-mega-menu__panel",
  };

  /**
   * Cached DOM elements.
   *
   * @type {object}
   */
  let ELEMENTS;
  let initialized = false;

  /**
   * Initialises hover behaviour on each mega menu block.
   * A grace delay prevents the panel from closing when the
   * cursor travels from the trigger into the panel.
   *
   * @returns {void}
   *
   * @author Brenden Mersey
   */
  const bindMegaMenuHoverListeners = () => {
    if (!ELEMENTS.megaMenus.length) {
      LOGGING &&
        console.warn(
          `[${SETTINGS.name}] :: bindMegaMenuHoverListeners() :: No mega menu elements found.`,
        );
      return;
    }

    let activeMenu = null;
    let closeTimeout = null;

    ELEMENTS.megaMenus.forEach((menu) => {
      menu.addEventListener("mouseenter", () => {
        clearTimeout(closeTimeout);
        if (activeMenu && activeMenu !== menu) {
          activeMenu.removeAttribute("data-open");
        }
        activeMenu = menu;
        menu.setAttribute("data-open", "");
      });

      menu.addEventListener("mouseleave", () => {
        closeTimeout = setTimeout(() => {
          menu.removeAttribute("data-open");
          activeMenu = null;
        }, SETTINGS.megaMenuCloseDelay);
      });
    });

    LOGGING &&
      console.log(
        `[${SETTINGS.name}] :: bindMegaMenuHoverListeners() :: Complete.`,
      );
  };

  /**
   * Tracks the height of the site header wrapper via ResizeObserver and writes
   * it to --bn3th-site-header-height on :root. Used by the mega menu panel and
   * main content offset so both respond to announcement bar size changes.
   *
   * @returns {void}
   */
  const observeSiteHeaderHeight = () => {
    if (!ELEMENTS.siteHeader) {
      LOGGING &&
        console.warn(
          `[${SETTINGS.name}] :: observeSiteHeaderHeight() :: Site header not found.`,
        );
      return;
    }

    const setHeight = () => {
      document.documentElement.style.setProperty(
        "--bn3th-site-header-height",
        `${ELEMENTS.siteHeader.offsetHeight}px`,
      );
    };

    new ResizeObserver(setHeight).observe(ELEMENTS.siteHeader);
    setHeight();

    LOGGING &&
      console.log(
        `[${SETTINGS.name}] :: observeSiteHeaderHeight() :: Complete.`,
      );
  };

  /**
   * Keeps the cart count dot on the header cart icon in sync with the live cart.
   * Pipeline's Totals class handles this for the "header" section type only,
   * so we replicate it here for "bn3th-header".
   *
   * @returns {void}
   */
  const bindCartCount = () => {
    const dots = document.querySelectorAll("[data-header-cart-full]");
    if (!dots.length) {
      LOGGING && console.warn(`[${SETTINGS.name}] :: bindCartCount() :: No cart count elements found.`);
      return;
    }

    document.addEventListener("theme:cart:change", (e) => {
      const cart = e.detail?.cart;
      if (!cart) return;

      const full = cart.item_count > 0;
      dots.forEach((dot) => {
        dot.setAttribute("data-header-cart-full", full);
        dot.setAttribute("data-cart-count", cart.item_count);
      });
    });

    LOGGING && console.log(`[${SETTINGS.name}] :: bindCartCount() :: Complete.`);
  };

  /**
   * Handles the product add popdown since Pipeline's CartPopdown handler only
   * initialises for sections of type "header", not "bn3th-header".
   * Listens for theme:cart:popdown, fetches the rendered popdown section, and
   * injects it into [data-product-add-popdown-wrapper].
   *
   * @returns {void}
   */
  const bindCartPopdown = () => {
    const popdown = document.querySelector("[data-product-add-popdown-wrapper]");
    if (!popdown) {
      LOGGING && console.warn(`[${SETTINGS.name}] :: bindCartPopdown() :: Popdown wrapper not found.`);
      return;
    }

    let dismissTimer = null;

    const dismiss = () => {
      popdown.classList.remove("is-visible");
    };

    document.addEventListener("theme:cart:popdown", (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;

      const url = `${window.theme.routes.root_url}variants/${variant.id}/?section_id=api-product-popdown`;

      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const node = document.createElement("div");
          node.innerHTML = html;
          const content = node.querySelector("[data-api-content]");
          if (content) popdown.innerHTML = content.innerHTML;

          popdown.classList.add("is-visible");

          const closer = popdown.querySelector("[data-close-popdown]");
          closer?.addEventListener("click", (e) => {
            e.preventDefault();
            dismiss();
          });

          clearTimeout(dismissTimer);
          dismissTimer = setTimeout(dismiss, 5000);
        })
        .catch((err) => {
          LOGGING && console.warn(`[${SETTINGS.name}] :: bindCartPopdown() :: Fetch failed`, err);
        });
    });

    LOGGING && console.log(`[${SETTINGS.name}] :: bindCartPopdown() :: Complete.`);
  };

  /**
   * Wires the cart drawer open/close since Pipeline's drawer handler only
   * initialises for sections of type "header", not "bn3th-header".
   *
   * @returns {void}
   */
  const bindCartDrawer = () => {
    const drawer = document.querySelector("[data-drawer=\"drawer-cart\"]");
    if (!drawer) {
      LOGGING && console.warn(`[${SETTINGS.name}] :: bindCartDrawer() :: Cart drawer not found.`);
      return;
    }

    const scrolls = drawer.querySelector("[data-drawer-scrolls]");
    const underlay = drawer.querySelector("[data-drawer-underlay]");

    const el = {
      form: drawer.querySelector("[data-cart-form]"),
      loader: drawer.querySelector("[data-cart-loading]"),
      empty: drawer.querySelector("[data-cart-empty]"),
      bottom: drawer.querySelector("[data-cart-bottom]"),
      items: drawer.querySelector("[data-line-items]"),
      subtotal: drawer.querySelector("[data-cart-subtotal]"),
    };

    const showEmpty = () => {
      el.empty?.classList.remove("cart--hidden");
      el.loader?.classList.remove("cart--loading");
      el.form?.classList.add("cart--hidden");
      el.bottom?.classList.add("cart--hidden");
    };

    const showForm = () => {
      el.form?.classList.remove("cart--hidden");
      el.loader?.classList.remove("cart--loading");
      el.empty?.classList.add("cart--hidden");
      el.bottom?.classList.remove("cart--hidden");
    };

    const updateCartSubtotal = () => {
      fetch("/?section_id=api-cart-subtotal")
        .then((r) => r.text())
        .then((html) => {
          const node = document.createElement("div");
          node.innerHTML = html;
          const content = node.querySelector("[data-api-content]");
          if (el.subtotal && content) el.subtotal.innerHTML = content.innerHTML;
        });
    };

    const updateCartItem = (key, quantity, itemEl) => {
      const loadbar = itemEl?.querySelector(".item--loadbar");
      if (loadbar) loadbar.style.display = "block";
      el.loader?.classList.add("cart--loading");

      fetch("/cart.js")
        .then((r) => r.json())
        .then((cart) => {
          const index = cart.items.findIndex((item) => item.key === key);
          if (index === -1) throw new Error(`Cart key not found: ${key}`);
          return fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ line: index + 1, quantity }),
          });
        })
        .then((r) => r.json())
        .then((cart) => {
          document.dispatchEvent(
            new CustomEvent("theme:cart:change", { detail: { cart }, bubbles: true })
          );
          loadCartContent();
        })
        .catch((err) => {
          LOGGING && console.warn(`[${SETTINGS.name}] :: updateCartItem() :: Failed`, err);
          loadCartContent();
        });
    };

    const initCartInteractions = () => {
      drawer.querySelectorAll("[data-quantity-selector]").forEach((wrapper) => {
        const increase = wrapper.querySelector("[data-increase-quantity]");
        const decrease = wrapper.querySelector("[data-decrease-quantity]");
        const input = wrapper.querySelector("[data-quantity-input]");
        if (!increase || !decrease || !input) return;

        const min = parseInt(input.getAttribute("min"), 10) || 0;

        increase.addEventListener("click", (e) => {
          e.preventDefault();
          let v = parseInt(input.value, 10) || 0;
          input.value = ++v;
          input.dispatchEvent(new Event("change"));
        });

        decrease.addEventListener("click", (e) => {
          e.preventDefault();
          let v = parseInt(input.value, 10) || 0;
          v = Math.max(min, --v);
          input.value = v;
          input.dispatchEvent(new Event("change"));
        });
      });

      drawer.querySelectorAll("[data-update-cart]").forEach((input) => {
        const key = input.getAttribute("data-update-cart");
        input.addEventListener("change", (e) => {
          const quantity = parseInt(e.target.value, 10);
          updateCartItem(key, quantity, e.target.closest("[data-cart-item]"));
        });
      });

      drawer.querySelectorAll("[data-remove-key]").forEach((btn) => {
        const key = btn.getAttribute("data-remove-key");
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          updateCartItem(key, 0, e.target.closest("[data-cart-item]"));
        });
      });
    };

    const loadCartContent = () => {
      fetch("/cart.js")
        .then((r) => r.json())
        .then((cart) => {
          if (cart.item_count === 0) {
            showEmpty();
          } else {
            fetch("/?section_id=api-cart-items")
              .then((r) => r.text())
              .then((html) => {
                const node = document.createElement("div");
                node.innerHTML = html;
                const content = node.querySelector("[data-api-content]");
                if (el.items && content) el.items.innerHTML = content.innerHTML;
                showForm();
                updateCartSubtotal();
                initCartInteractions();
              });
          }
        })
        .catch((err) => {
          LOGGING && console.warn(`[${SETTINGS.name}] :: loadCartContent() :: Failed`, err);
        });
    };

    const formatMoney = (cents, format) => {
      const fmt = format || "${{amount}}";
      const withDelimiters = (num, precision = 2, thousands = ",", decimal = ".") => {
        if (isNaN(num) || num == null) return "0";
        const fixed = (num / 100).toFixed(precision);
        const parts = fixed.split(".");
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        return dollars + (parts[1] ? decimal + parts[1] : "");
      };
      const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!match) return String(cents);
      let value = "";
      switch (match[1]) {
        case "amount":                              value = withDelimiters(cents, 2); break;
        case "amount_no_decimals":                  value = withDelimiters(cents, 0); break;
        case "amount_with_comma_separator":         value = withDelimiters(cents, 2, ".", ","); break;
        case "amount_no_decimals_with_comma_separator": value = withDelimiters(cents, 0, ".", ","); break;
        case "amount_no_decimals_with_space_separator": value = withDelimiters(cents, 0, " "); break;
        case "amount_with_apostrophe_separator":    value = withDelimiters(cents, 2, "'"); break;
        default:                                    value = withDelimiters(cents, 2);
      }
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
    };

    const bindShippingMessage = () => {
      const message = drawer.querySelector("[data-cart-message]");
      if (!message) return;

      const limit = Number(message.getAttribute("data-limit")) * 100;
      const circumference = 28 * Math.PI;

      const setProgress = (circle, percent) => {
        const offset = circumference - ((percent / 100) * circumference) / 2;
        circle.style.strokeDashoffset = offset;
      };

      drawer.querySelectorAll("[data-cart-progress]").forEach((circle) => {
        setProgress(circle, Number(circle.getAttribute("data-percent")) || 0);
      });

      document.addEventListener("theme:cart:change", (e) => {
        const cart = e.detail?.cart;
        if (!cart) return;

        const total = cart.total_price;
        const hasFreeShippingMessage = message.getAttribute("data-cart-message") === "true";

        if (total >= limit) {
          message.classList.toggle("is-success", hasFreeShippingMessage && total !== 0);
          message.classList.toggle("is-hidden", !hasFreeShippingMessage || total === 0);
        } else {
          message.classList.remove("is-success", "is-hidden");
        }

        const remaining = Math.max(limit - total, 0);
        const leftToSpend = formatMoney(remaining, window.theme?.moneyFormat).replace(".00", "");
        drawer.querySelectorAll("[data-left-to-spend]").forEach((el) => {
          el.innerHTML = leftToSpend;
        });

        const percent = Math.min((total / limit) * 100, 100);
        drawer.querySelectorAll("[data-cart-progress]").forEach((circle) => {
          setProgress(circle, percent);
        });
      });

      LOGGING && console.log(`[${SETTINGS.name}] :: bindShippingMessage() :: Complete.`);
    };

    bindShippingMessage();

    const open = () => {
      drawer.classList.remove("display-none");
      setTimeout(() => {
        drawer.classList.add("drawer--visible");
        scrolls?.dispatchEvent(new CustomEvent("theme:scroll:lock", { bubbles: true }));
        loadCartContent();
      }, 1);
    };

    const close = () => {
      drawer.classList.remove("drawer--visible");
      scrolls?.dispatchEvent(new CustomEvent("theme:scroll:unlock", { bubbles: true }));
      setTimeout(() => {
        if (!drawer.classList.contains("drawer--visible")) {
          drawer.classList.add("display-none");
        }
      }, 800);
    };

    // Event delegation handles the header cart icon and any dynamically injected
    // toggles (e.g. the "View Cart" button in the product add popdown).
    document.addEventListener("click", (e) => {
      const toggle = e.target.closest("[data-drawer-toggle=\"drawer-cart\"]");
      if (!toggle || !drawer.isConnected) return;
      e.preventDefault();
      drawer.classList.contains("drawer--visible") ? close() : open();
    });

    // Pipeline's CartPopdown dispatches theme:drawer:open directly on the drawer
    // element when "View Cart" is clicked. Catch it here since CartPopdown is
    // registered under the "header" section type and never initialises for us.
    drawer.addEventListener("theme:drawer:open", open);

    underlay?.addEventListener("click", close);

    drawer.addEventListener("keyup", (e) => {
      if (e.key === "Escape") close();
    });

    LOGGING && console.log(`[${SETTINGS.name}] :: bindCartDrawer() :: Complete.`);
  };

  /**
   * Initialises the Bn3thHeader singleton.
   *
   * @returns {void}
   *
   * @author Brenden Mersey
   */
  const init = () => {
    if (initialized || !document.querySelector(".bn3th-header")) return;
    initialized = true;
    ELEMENTS = {
      siteHeader: document.querySelector(SELECTORS.siteHeader),
      header: document.querySelector(".bn3th-header"),
      nav: document.querySelector(".bn3th-header__nav"),
      megaMenus: document.querySelectorAll(SELECTORS.megaMenu),
      actions: {
        search: document.querySelector(".bn3th-header__actions-item.search"),
        cart: document.querySelector(".bn3th-header__actions-item.cart"),
        account: document.querySelector(".bn3th-header__actions-item.account"),
        locale: document.querySelector(".bn3th-header__actions-item.locale"),
      },
    };
    LOGGING && console.log(`[${SETTINGS.name}] :: Start`);

    observeSiteHeaderHeight();
    bindMegaMenuHoverListeners();
    bindCartDrawer();
    bindCartPopdown();
    bindCartCount();
    BN3THSearch.create().init();

    LOGGING && console.log(`[${SETTINGS.name}] :: Complete`);
  };

  return { init };
})();

document.addEventListener("bn3th:navigation:modern-ready", Bn3thHeader.init);
Bn3thHeader.init();
